import { computed, ref, toRef, type MaybeRef } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import chitService from '@/services/chit/chit'
import { Chit, ChitComment, ChitOption, type IChitCommentData, type IChitData } from '@/models/chit'
import { useSnackbarStore } from '@/stores'
import type { IChitFilterParam, IChitSubmitParams } from '@/services'

export interface ICreateChitCommentParams {
  chitNumber: number
  data: IChitCommentData
}

export function useFetchChits(chitFilter?: IChitFilterParam) {
  const chitFilterRef = toRef([chitFilter, 'chits'])

  const queryKey = ref(['chits', chitFilterRef])

  const count = ref<number>(0)

  const {
    isLoading,
    isError,
    error,
    data: chits
  } = useQuery({
    // To be able to call chits it must have a chit and the permission
    enabled: computed(() => !!chitFilterRef.value),
    queryKey: queryKey,
    queryFn: () => chitService.fetchChits(chitFilter),

    select: (data) => {
      count.value = data.count
      return data.results.map((chit) => new Chit(chit))
    }
  })

  return { queryKey, chits, error, isError, isLoading, count }
}

export function useFetchChit(chitId: MaybeRef<number>) {
  const chitIdRef = toRef(chitId)

  const queryKey = ref(['chits', chitIdRef])

  const {
    data: chit,
    isPending,
    error
  } = useQuery({
    queryKey: queryKey,
    queryFn: () => chitService.fetchChit(chitIdRef.value),
    select: (data) => new Chit(data)
  })

  return { chit, isPending, error, queryKey }
}

export function useCreateChit() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['create-chit'],
    mutationFn: (chit: IChitData) => chitService.createChit(chit),

    onSuccess: (data) => {
      queryClient.setQueryData(['chit', data.chit_number], data)
      queryClient.invalidateQueries({ queryKey: ref(['chits']) })
      snackbarStore.showSnackbar('Chit created successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useUpdateChit() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-chit'],
    mutationFn: (chit: IChitData) => chitService.updateChit(chit),

    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ref(['chit', data.chit_number]) })
      queryClient.invalidateQueries({ queryKey: ref(['chits']) })
      snackbarStore.showSnackbar('Chit updated successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useDeleteChit() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ref(['delete-chit']),
    mutationFn: (id: number) => chitService.deleteChit(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['chits']) })
      snackbarStore.showSnackbar('Chit deleted successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useFetchChitOptions(type?: string) {
  const queryKey = ref(['chit-options', type])

  const {
    data: chitOptions,
    isPending,
    error
  } = useQuery({
    queryKey: queryKey,
    queryFn: () => chitService.fetchChitOptions(type),
    select: (data) => data.map((chitOption) => new ChitOption(chitOption))
  })

  return { chitOptions, isPending, error, queryKey }
}

export function useExportChitExcel() {
  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['export-chit-excel'],
    mutationFn: (chitFilter?: IChitFilterParam) => chitService.exportChitExcel(chitFilter),
    onSuccess: () => {
      snackbarStore.showSnackbar('Chit exported successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useExportChitPdf() {
  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['export-chit-pdf'],
    mutationFn: (chitNumber: number) => chitService.exportChitPdf(chitNumber),

    onSuccess: () => {
      snackbarStore.showSnackbar('Chit exported successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useSubmitChit() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['submit-chit'],
    mutationFn: (payload: IChitSubmitParams) => chitService.submitChit(payload),

    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ref(['chit', data.chit_number]) })
      snackbarStore.showSnackbar('Chit submitted successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useApproveChit() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['approve-chit'],
    mutationFn: (id: number) => chitService.approveChit(id),

    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ref(['chit', data.chit_number]) })
      snackbarStore.showSnackbar('Chit approved successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useFetchChitComments(chitNumber: MaybeRef<number>) {
  const chitNumberRef = toRef(chitNumber)

  const queryKey = ref(['chit-comments', chitNumberRef])

  const {
    data: comments,
    isPending,
    error
  } = useQuery({
    queryKey: queryKey,
    queryFn: () => chitService.fetchChitComments(chitNumberRef.value),
    select: (data) => data.map((comment) => new ChitComment(comment))
  })

  return { comments, isPending, error, queryKey }
}

export function useFetchChitComment(chitNumber: MaybeRef<number>, commentId: MaybeRef<number>) {
  const chitNumberRef = toRef(chitNumber)
  const commentIdRef = toRef(commentId)

  const queryKey = ref(['chit-comment', chitNumberRef, commentIdRef])

  const {
    data: comment,
    isPending,
    error
  } = useQuery({
    queryKey: queryKey,
    queryFn: () => chitService.fetchChitComment(chitNumberRef.value, commentIdRef.value),
    select: (data) => new ChitComment(data)
  })

  return { comment, isPending, error, queryKey }
}

export function useCreateChitComment() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const chitNumberRef = ref<number>()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['create-chit-comment'],
    mutationFn: (data: ICreateChitCommentParams) => {
      chitNumberRef.value = data.chitNumber
      return chitService.createChitComment(data.chitNumber, data.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['chit-comments', chitNumberRef.value]) })
      snackbarStore.showSnackbar('Comment created successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useUpdateChitComment(chitNumber: MaybeRef<number>) {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const chitNumberRef = toRef(chitNumber)

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-chit-comment', chitNumberRef],
    mutationFn: (data: IChitCommentData) =>
      chitService.updateChitComment(chitNumberRef.value, data),

    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ref(['chit-comments', chitNumberRef.value, data.id])
      })
      queryClient.invalidateQueries({
        queryKey: ref(['chit-comments', chitNumberRef.value])
      })
      snackbarStore.showSnackbar('Comment updated successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useDeleteChitComment(chitNumber: MaybeRef<number>) {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const chitNumberRef = toRef(chitNumber)

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['delete-chit-comment', chitNumberRef],
    mutationFn: (id: number) => chitService.deleteChitComment(chitNumberRef.value, id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['chit-comments', chitNumberRef.value]) })
      snackbarStore.showSnackbar('Comment deleted successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}
